import { isAfter, isEqual } from 'date-fns'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIGetPelaksanaanConfig,
  useAPIPelaksanaanSchools,
} from 'api/kinerja/useAPIKinerja'
import {
  isKsDefinitifPlt,
  isUserHasKsRole,
} from 'app/KinerjaKSPage/utils/profile'
import { BannerPltConfirmation } from 'app/KinerjaPage/components/BannerPltConfirmation'
import CardKinerjaPeriksa from 'app/KinerjaPage/components/CardKinerjaPeriksa'
import { DialogPltKSConfirmation } from 'app/KinerjaPage/components/DialogPltKSConfirmation'
import { DialogSelectSchoolPerencanaan } from 'app/KinerjaPage/components/DialogSelectSchoolPerencanaan'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import CardTahapPenilaian from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardTahapPenilaian'
import CardKSStartPeriod from 'app/KinerjaPage/components/CardKSStartPeriod'
import CardKinerjaPeriksaMultipleSchool from '../CardKinerjaPeriksaMultipleSchool'
import { isDateValid } from 'utils/datetime'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  getPelaksanaanDate,
  isDateStarted,
} from 'app/KinerjaShared/utils/pelaksanaan'
import { BannerPelaksanaanNotStarted } from 'app/KinerjaPage/components/BannerPelaksanaanNotStarted'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'
import CardPenetapanPredikatKinerjaTahunan from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardPenetapanPredikatKinerjaTahunan'
import useToggleSdkContext from 'utils/useToogleSdk'
import { BannerPenetapanPredikatKinerjaTahunan } from 'app/KinerjaPage/pages/PelaksanaanV2/components/BannerPenetapanPredikatKinerjaTahunan'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { CardKSSchool } from 'app/KinerjaShared/components/CardKSSchool'
import {
  isKsSPNFDefinitifPlt,
  isUserHasKsSPNFRole,
} from 'app/KinerjaKSPNFPage/utils/profile'

import type {
  KinerjaHeadmasterCommonRoleType,
  KinerjaProfil,
} from 'types/kinerja'

interface KepalaSekolahRootViewProps {
  isLoading?: boolean
  profile: KinerjaProfil
  isKinerjaPelaksanaanEnabled?: boolean

  baseUrl?: string
  roleType?: KinerjaHeadmasterCommonRoleType
  additionalCardProps?: {
    title?: string
    badgeText?: string
  }
}

export default function KepalaSekolahRootView({
  isLoading = false,
  profile,
  isKinerjaPelaksanaanEnabled,
  baseUrl = PENGELOLAAN_KINERJA_KS_PENILAI_PATH,
  roleType = 'KS',
  additionalCardProps,
}: KepalaSekolahRootViewProps) {
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const {
    periods,
    period: periodData,
    periodNo,
    periodYear,
  } = usePeriodContext()
  const toggleValue = useToggleSdkContext()

  const [showDialogPerencanaan, setShowDialogPerencanaan] = useState(false)
  const [showDialogPltConfirmation, setShowDialogPltConfirmation] =
    useState(false)

  const { data: dataPelaksanaanSchools, isLoading: isLoadingSchools } =
    useAPIPelaksanaanSchools({
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const handleShowConfirmationDialog = () => setShowDialogPltConfirmation(true)

  const { data: dataPelaksanaanConfig } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      // Disable error boundary since the data is not required for this page
      useErrorBoundary: false,
    },
  })

  // Period-related vars
  const isPeriodResolved = Array.isArray(periods) && periods.length > 0
  const pelaksanaanDate = getPelaksanaanDate(periodData.periodeKinerja)
  const isPelaksanaanStarted =
    isPeriodResolved && isDateStarted(periodData.periodeKinerja)
  const period = periodData.period

  // Check if there is unconfirmed sekolah negeri
  const haveUnconfirmedPublicSchools = dataPelaksanaanSchools?.data?.some(
    (thisSchool) =>
      thisSchool.statusSatuanPendidikan == 'NEGERI' &&
      thisSchool.confirmationStatus == 'UNCONFIRMED'
  )

  const checkKsRoleFn =
    roleType === 'KS' ? isUserHasKsRole : isUserHasKsSPNFRole
  const checkKsDefinitifPltFn =
    roleType === 'KS' ? isKsDefinitifPlt : isKsSPNFDefinitifPlt

  // if all schools under the current user (KS) is on Pelaksanaan phase
  const isAllSchoolsCompleted =
    checkKsRoleFn(profile) &&
    !!dataPelaksanaanSchools?.data?.every(
      (thisSchool) => thisSchool.perencanaanReadyState === 'COMPLETED'
    )

  // check for if is all school perencanaan state is empty (not started at all), if true display list periksa (perencanaan)
  const isAllSchoolEmpty =
    checkKsDefinitifPltFn(profile) &&
    !!dataPelaksanaanSchools?.data?.every(
      (thisSchool) => thisSchool.perencanaanReadyState == 'EMPTY'
    )

  const RenderBannerPltConfirmation = () =>
    checkKsDefinitifPltFn(profile) &&
    !isLoadingSchools &&
    haveUnconfirmedPublicSchools && <BannerPltConfirmation />

  const handleTrackLakukanPenilaian = () => {
    trackEvent({
      activity_type: 'penilaian_ks_home_click_lakukan_penilaian',
      screen_name: 'ks_penilaian_home',
      event_trigger: 'ks_penilaian_home',
    })
  }

  // Tahap penilaian
  const showTahapPenilaian = useMemo(() => {
    // access date
    const accessDate =
      dataPelaksanaanConfig?.data?.organizationProgressAccessDate
    const isValidAccessDate = isDateValid(accessDate)
    const currentDate = new Date()
    const accessDateObject = new Date(accessDate)

    return (
      isPelaksanaanStarted &&
      isValidAccessDate &&
      (isAfter(currentDate, accessDateObject) ||
        isEqual(currentDate, accessDateObject))
    )
  }, [
    dataPelaksanaanConfig?.data?.organizationProgressAccessDate,
    isPelaksanaanStarted,
  ])

  // Penetapan Predikat Kinerja Tahunan
  const penetapanPredikatTahunanCount =
    dataPelaksanaanSchools?.meta?.predikatReadyTotal ?? 0
  const isPenetapanPredikatTahunanEnabled =
    toggleValue?.['kinerja-penetapan-predikat-kinerja-tahunan-enabled'] === true
  const hasPKO = dataPelaksanaanSchools?.meta?.predikatKinerjaOrganisasiReady
  const isAllPredikatTahunanSent =
    dataPelaksanaanSchools?.meta?.predikatAnnualCompleted
  const showPenetapanPredikatTahunanCard =
    periodNo === 2 && isPenetapanPredikatTahunanEnabled && hasPKO

  const teacherLabel = roleType === 'KSPNF' ? 'Pamong' : 'Guru'

  const RenderSchools = () => (
    <>
      {dataPelaksanaanSchools?.data
        ?.filter((school) => school.perencanaanReadyState != 'EMPTY')
        .map((school) => {
          const nextPath = `${baseUrl}/penilaian-kinerja/${school.npsn}`

          return (
            <CardKSSchool
              key={school.npsn}
              nextText="Lakukan"
              nextPath={nextPath}
              confirmationPath={
                school.statusSatuanPendidikan === 'SWASTA'
                  ? `${baseUrl}/konfirmasi-swasta/${school.npsn}?successPath=${nextPath}`
                  : undefined
              }
              onConfirmClick={
                school.statusSatuanPendidikan === 'NEGERI'
                  ? handleShowConfirmationDialog
                  : undefined
              }
              onNextClick={handleTrackLakukanPenilaian}
              confirmationStatus={school.confirmationStatus}
              schoolName={school.schoolName}
              schoolStatus={school.statusSatuanPendidikan}
              schoolRole={school.role}
              npsn={school.npsn}
            />
          )
        })}

      <DialogPltKSConfirmation
        isOpen={showDialogPltConfirmation}
        onClose={() => setShowDialogPltConfirmation(false)}
      />
    </>
  )

  useEffect(() => {
    if (isKinerjaPelaksanaanEnabled && !isAllSchoolEmpty) {
      trackEvent({
        activity_type: 'penilaian_ks_open_home',
        screen_name: 'ks_penilaian_home',
        event_trigger: 'ks_penilaian_home',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKinerjaPelaksanaanEnabled, isAllSchoolEmpty])

  if (isLoadingSchools)
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner data-testid="loading-spinner" />
      </div>
    )

  if (isKinerjaPelaksanaanEnabled && !isAllSchoolEmpty)
    return (
      <>
        <CardKSStartPeriod
          isMultipleSchoolEnabled
          schools={dataPelaksanaanSchools?.data}
        />

        <BannerPenetapanPredikatKinerjaTahunan prevYear={periodYear - 1} />

        {showPenetapanPredikatTahunanCard && (
          <CardPenetapanPredikatKinerjaTahunan
            hasPKO={hasPKO}
            isAllSent={isAllPredikatTahunanSent}
            itemsToSend={penetapanPredikatTahunanCount}
            schools={dataPelaksanaanSchools?.data ?? []}
          />
        )}

        {!isPelaksanaanStarted && (
          <BannerPelaksanaanNotStarted
            periodNo={periodNo}
            pelaksanaanDate={pelaksanaanDate}
          />
        )}

        {showTahapPenilaian && <CardTahapPenilaian />}
        <RenderBannerPltConfirmation />
        {checkKsRoleFn(profile) && !isAllSchoolsCompleted && (
          <Card>
            <div className="flex gap-6 p-6">
              <div className="flex-1 flex flex-col gap-1 justify-center">
                <div className="flex items-center gap-2">
                  <Text variant="heading-md">
                    Ada Perencanaan {teacherLabel} yang belum selesai
                  </Text>
                </div>
                <Text color="subdued">
                  Sebelum lanjut ke tahap Penilaian, selesaikan tahap
                  Perencanaan {teacherLabel} terlebih dahulu.
                </Text>
              </div>
              <div className="flex flex-col justify-center">
                {isLoading ? (
                  <Shimmer />
                ) : (
                  <Button
                    color="white"
                    as="a"
                    onClick={() => setShowDialogPerencanaan(true)}
                  >
                    Ke Perencanaan {teacherLabel} <Icon>chevron_right</Icon>
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}
        {isPelaksanaanStarted && (
          <Card>
            <div className="flex gap-6 p-6">
              <div className="flex flex-col">
                <Image
                  alt=""
                  src="/illustrations/img-book-rapor-graphic.png"
                  width={84}
                  height={84}
                />
              </div>
              <div className="flex-1 flex flex-col gap-1 justify-center">
                {periodYear < 2025 && (
                  <Text
                    variant="subheading"
                    color="informational"
                    className="!text-blue-60 !uppercase tracking-[0.5px]"
                  >
                    <CardPeriodType type="pelaksanaan" />
                  </Text>
                )}
                <div className="flex items-center gap-2">
                  <Text variant="heading-lg">
                    Penilaian Kinerja dan Penetapan Predikat Kinerja
                  </Text>
                  <Badge className="uppercase whitespace-nowrap">
                    {additionalCardProps?.badgeText || 'Khusus Kepala Sekolah'}
                  </Badge>
                </div>
                <Text color="subdued">
                  Lakukan untuk seluruh {teacherLabel} di satuan pendidikan
                  Anda.
                </Text>
              </div>
            </div>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(400px,1fr))] px-6 gap-6">
              {isLoading ? (
                <>
                  <Shimmer height="120px" width="100%" />
                  <Shimmer height="120px" width="100%" />
                </>
              ) : (
                <RenderSchools />
              )}
            </div>

            <div className="rounded bg-cyan-5 p-3 mt-6 flex items-center m-6">
              <Icon color="informational" className="mr-1">
                error
              </Icon>
              Jika satuan pendidikan tidak sesuai, mohon kunjungi&nbsp;
              <a
                className="!text-blue-70 underline"
                href={PANDUAN_PENGELOLAAN_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_home_click_pusat_bantuan',
                    screen_name: 'ks_penilaian_home',
                    event_trigger: 'ks_penilaian_home',
                  })
                }}
              >
                Pusat Bantuan
              </a>
              .
            </div>
          </Card>
        )}
        <DialogSelectSchoolPerencanaan
          baseUrl={baseUrl}
          schools={dataPelaksanaanSchools?.data}
          isOpen={showDialogPerencanaan}
          onClose={() => setShowDialogPerencanaan(false)}
        />
      </>
    )

  if (isAllSchoolEmpty) {
    return (
      <>
        <CardKSStartPeriod
          isMultipleSchoolEnabled
          schools={dataPelaksanaanSchools?.data}
        />

        <BannerPenetapanPredikatKinerjaTahunan prevYear={periodYear - 1} />

        {showPenetapanPredikatTahunanCard && (
          <CardPenetapanPredikatKinerjaTahunan
            hasPKO={hasPKO}
            isAllSent={isAllPredikatTahunanSent}
            itemsToSend={penetapanPredikatTahunanCount}
            schools={dataPelaksanaanSchools?.data ?? []}
          />
        )}

        {showTahapPenilaian && <CardTahapPenilaian />}

        <RenderBannerPltConfirmation />
        <CardKinerjaPeriksaMultipleSchool
          schools={dataPelaksanaanSchools?.data}
          isLoading={isLoading}
          baseUrl={baseUrl}
          roleType={roleType}
          subtitle={`Cek Perencanaan yang sudah diajukan oleh ${teacherLabel}.`}
          {...additionalCardProps}
        />
      </>
    )
  }

  return (
    <>
      <CardKSStartPeriod
        isMultipleSchoolEnabled
        schools={dataPelaksanaanSchools?.data}
      />

      <BannerPenetapanPredikatKinerjaTahunan prevYear={periodYear - 1} />

      {showPenetapanPredikatTahunanCard && (
        <CardPenetapanPredikatKinerjaTahunan
          hasPKO={hasPKO}
          isAllSent={isAllPredikatTahunanSent}
          itemsToSend={penetapanPredikatTahunanCount}
          schools={dataPelaksanaanSchools?.data ?? []}
        />
      )}

      {showTahapPenilaian && <CardTahapPenilaian />}

      <RenderBannerPltConfirmation />
      <CardKinerjaPeriksa
        baseUrl={baseUrl}
        period={period}
        profile={profile}
        isLoading={isLoading}
        subtitle={`Cek Perencanaan yang sudah diajukan oleh ${teacherLabel}.`}
        {...additionalCardProps}
      />
    </>
  )
}
